import { Box, Icon } from '@palmetto/palmetto-components';
import React from 'react';

import { DIFF_IDENTITY_KEY, DiffArrayElement, DiffNode, DiffRecord, isDiffLeaf, REDACTED_DIFF_VALUE } from 'types';

import { camelCaseToTitleCase } from '../../../helpers/camelCaseToTitleCase';
import { normalizeDiffDisplayValue } from '../../../helpers/normalizeDiffDisplayValue';

const isPlainObject = (value: any): boolean => value !== null && typeof value === 'object' && !Array.isArray(value);

const getDiffIdentity = (element: any): string | undefined =>
  isPlainObject(element) && element[DIFF_IDENTITY_KEY] != null ? String(element[DIFF_IDENTITY_KEY]) : undefined;

// Drop the synthetic identity marker so it is not rendered as a changed field.
const stripDiffIdentity = (element: any): any => {
  if (isPlainObject(element) && DIFF_IDENTITY_KEY in element) {
    const rest = { ...element };
    delete rest[DIFF_IDENTITY_KEY];
    return rest;
  }
  return element;
};

// Render a from/to value for display. Primitives pass straight through (unchanged
// behavior); a whole object — e.g. an added/removed sub-object that arrives as
// { from: null, to: <wholeObject> } — is rendered recursively as indented
// `Field: value` lines instead of a raw JSON.stringify blob.
const renderDiffDisplayValue = (value: any): React.ReactNode => {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    return (
      <Box padding="0 0 0 md" childGap="2xs">
        {Object.entries(value).map(([key, nestedValue]) => (
          <Box key={key} direction="row" childGap="xs">
            <Box fontWeight="bold">{camelCaseToTitleCase(key)}:</Box>
            <Box>{renderDiffDisplayValue(normalizeDiffDisplayValue(nestedValue))}</Box>
          </Box>
        ))}
      </Box>
    );
  }
  return value;
};

// A diff leaf value can be a primitive, or a whole added/removed sub-object rendered
// recursively as Field: value lines — not just a string.
type DiffLeafValue = string | number | boolean | null | undefined | Record<string, unknown>;

export const DiffValue = ({
  property,
  from,
  to,
  hideInitialNulls,
}: {
  property: string;
  from: DiffLeafValue;
  to: DiffLeafValue;
  hideInitialNulls?: boolean;
}) => {
  const normalizedFrom = normalizeDiffDisplayValue(from);
  const normalizedTo = normalizeDiffDisplayValue(to);
  // `== null` catches a real null/undefined; the string forms catch a value already stringified
  // upstream. normalizeDiffDisplayValue passes undefined straight through, so checking only the
  // string forms would miss it — this matches canSkip's avoidableValues set below.
  const emptyFrom = normalizedFrom == null || ['undefined', 'null'].includes(normalizedFrom);
  const emptyTo = normalizedTo == null || ['undefined', 'null'].includes(normalizedTo);

  if (emptyFrom && emptyTo) {
    return null;
  }

  // SOC 2: the backend redacts sensitive field values to REDACTED. Show only that the
  // field changed — never a (hidden) value or arrow. Guard on EITHER side being the
  // marker so a value can never render alongside a redaction, even if the backend ever
  // emits a one-sided redaction.
  if (normalizedFrom === REDACTED_DIFF_VALUE || normalizedTo === REDACTED_DIFF_VALUE) {
    return (
      <Box direction="row" alignItems="center" childGap="xs" margin="0 0 xs 0">
        <Box fontWeight="bold">{camelCaseToTitleCase(property)}</Box>
        <Box color="body-secondary">changed (value hidden)</Box>
      </Box>
    );
  }

  const fromToDisplay = renderDiffDisplayValue(normalizedFrom);
  const toToDisplay = renderDiffDisplayValue(normalizedTo);
  const hideInitial = hideInitialNulls && (normalizedFrom == null || normalizedFrom === 'null');

  return (
    <>
      <Box display="inline-block" fontWeight="bold" margin="0 0 2xs 0">
        {camelCaseToTitleCase(property)}
      </Box>
      <Box direction="row" alignItems="center" childGap="xs" margin="0 0 xs 0">
        {!hideInitial && <Box>{fromToDisplay}</Box>}
        {!hideInitial && (
          <Box>
            <Icon name="arrow-right" />
          </Box>
        )}
        <Box>{toToDisplay}</Box>
      </Box>
    </>
  );
};

function canSkip(value1: any, value2: any) {
  const avoidableValues = new Set([undefined, 'undefined', null]);
  return avoidableValues.has(value1) && avoidableValues.has(value2);
}

type RenderContext = { hiddenProperties: string[]; hideInitialNulls?: boolean };

// A property is hidden by its bare name (`meta`) or by its full path from the root of the
// diff (`attestment.userId`). Array indices are not part of the path, so one dotted path
// covers the same field inside every element of an array.
const isHidden = (path: string[], { hiddenProperties }: RenderContext): boolean =>
  hiddenProperties.includes(path[path.length - 1]) || hiddenProperties.includes(path.join('.'));

// One element of a diffed array, at any depth. Index-matched arrays are sparse, so unchanged
// positions arrive as holes and render nothing. Identity-matched elements are labelled by their
// identity; the rest fall back to their 1-based position.
const renderArrayElement = (
  key: string,
  element: DiffArrayElement,
  index: number,
  path: string[],
  context: RenderContext,
): React.ReactNode => {
  if (element === null || element === undefined) {
    return null;
  }
  const identity = getDiffIdentity(element);
  const node = stripDiffIdentity(element) as DiffNode;
  const body = isDiffLeaf(node) ? (
    // The heading already names the element, so the value line carries no property of its own.
    <DiffValue
      property=""
      from={(node.from ?? null) as DiffLeafValue}
      to={(node.to ?? null) as DiffLeafValue}
      hideInitialNulls={context.hideInitialNulls}
    />
  ) : (
    renderNode(key, node, path, context, { headingless: true })
  );
  if (Array.isArray(body) && body.length === 0) {
    return null;
  }
  return (
    <Box key={`${key}_${index}`}>
      <Box fontWeight="bold" padding={'xs 0 0 0'}>
        {identity == null
          ? `${camelCaseToTitleCase(key)} #${index + 1}`
          : `${camelCaseToTitleCase(key)}: ${identity}`}
      </Box>
      {body}
    </Box>
  );
};

const renderNode = (
  key: string,
  node: DiffNode,
  path: string[],
  context: RenderContext,
  { headingless = false }: { headingless?: boolean } = {},
): React.ReactNode => {
  if (isDiffLeaf(node)) {
    if (canSkip(node.from, node.to)) {
      return null;
    }
    return (
      <Box key={key} childGap="xs">
        <DiffValue
          key={key}
          property={key}
          from={normalizeDiffDisplayValue(node.from)}
          to={normalizeDiffDisplayValue(node.to)}
          hideInitialNulls={context.hideInitialNulls}
        />
      </Box>
    );
  }

  if (Array.isArray(node)) {
    return (
      <Box key={key} childGap="xs">
        <Box fontWeight="bold">{camelCaseToTitleCase(key)}</Box>
        <Box padding="0 0 0 md">
          {node.map((element, index) => renderArrayElement(key, element, index, path, context))}
        </Box>
      </Box>
    );
  }

  const children = renderRecord(node, path, context);
  if (headingless) {
    return children;
  }
  return (
    <Box key={key} childGap="xs">
      <Box fontWeight="bold">{camelCaseToTitleCase(key)}</Box>
      <Box padding="0 0 0 md">{children}</Box>
    </Box>
  );
};

const renderRecord = (node: DiffRecord, path: string[], context: RenderContext): React.ReactNode[] =>
  Object.keys(node)
    .filter((key) => !isHidden([...path, key], context))
    .map((key) => {
      const child = node[key];
      // Defensive: a malformed or legacy diff can hold a non-object here, which has no
      // structure to walk and must not crash the whole history view.
      if (child === null || child === undefined || typeof child !== 'object') {
        return null;
      }
      return renderNode(key, child, [...path, key], context);
    });

export const Diffs = ({
  diff,
  hiddenProperties,
  hideInitialNulls,
}: {
  diff: DiffNode | undefined;
  hiddenProperties: string[];
  hideInitialNulls?: boolean;
}): React.ReactNode => {
  if (!diff) {
    return [];
  }
  const context: RenderContext = { hiddenProperties, hideInitialNulls };
  if (isDiffLeaf(diff)) {
    return renderNode('', diff, [], context);
  }
  if (Array.isArray(diff)) {
    return diff.map((element, index) => renderArrayElement('', element, index, [], context));
  }
  return renderRecord(diff, [], context);
};
